import { useQueryClient } from '@tanstack/react-query';
import { ComponentProps, useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { useMarketplacePaymentDataContext } from 'src/contexts/MarketplacePaymentDataContext';
import {
  PAYMENT_MONTHLY_CONTAINER_QUERY_KEY,
  PAYMENT_SINGLE_MONTH_QUERY_KEY,
} from 'src/contexts/MarketplacePaymentDataContext/MarketplacePaymentDataContext.constants';
import { vars } from 'src/core/themes';
import { Button } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { IconsFill, UploadIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  FileParameter,
  MarketplacePaymentClient,
  MarketplacePaymentQuery,
} from 'src/WebApiController';

import {
  BulkUploadMarketplacePaymentLineDialog,
  MarketplacePaymentUploadForm,
} from './BulkUploadMarketplacePaymentLineDialog';

export type LaunchBulkUploadMarketplacePaymentLineDialogProps = ComponentProps<
  typeof Button
> & { className?: string };

export const LaunchBulkUploadMarketplacePaymentLineDialog = ({
  variant = 'regular',
  ...props
}: LaunchBulkUploadMarketplacePaymentLineDialogProps) => {
  const bulkUploadMarketplacePaymentLineDialog = useBasicDialog();

  const onBulkUploadPayment = () => {
    bulkUploadMarketplacePaymentLineDialog.launchDialog();
  };

  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { refreshData } = useMarketplacePaymentDataContext();
  const { filterQuery } = useFilterQueryContext<MarketplacePaymentQuery>();
  const queryClient = useQueryClient();

  const handleUploadSubmit = useCallback(
    async (formValues: MarketplacePaymentUploadForm, file: File) => {
      return tryInvokeApi(
        async () => {
          const result = await new MarketplacePaymentClient(
            activeAccountWebClientConfig
          ).importMarketplacePaymentFromFile(
            {
              data: file,
              fileName: file!.name,
            },
            formValues.marketplace,
            formValues.paymentDate,
            formValues.externalPaymentId,
            formValues.paymentReferenceId,
            formValues.currency,
            formValues.paymentAmount.amt
          );

          if (result.success) {
            // Handle success
            if (result.warnings && result.warnings.length > 0) {
              showErrorDialog(
                'MarketplacePaymentClient.importMarketplacePaymentFromFile',
                {
                  message: result.warnings.join('\n'),
                },
                {
                  customerHeader: (
                    <Content id={ContentId.PaymentUploadedWithWarnings} />
                  ),
                }
              );
            }

            // refetch payment data if the upload is successful
            await queryClient.invalidateQueries({
              queryKey: [
                PAYMENT_MONTHLY_CONTAINER_QUERY_KEY,
                activeAccountWebClientConfig.activeAccountId,
              ],
            });
            await queryClient.invalidateQueries({
              queryKey: [
                PAYMENT_MONTHLY_CONTAINER_QUERY_KEY,
                activeAccountWebClientConfig.activeAccountId,
                filterQuery,
              ],
            });
            await queryClient.invalidateQueries({
              queryKey: [PAYMENT_SINGLE_MONTH_QUERY_KEY, true],
              type: 'all',
            });
            await refreshData(true);
            bulkUploadMarketplacePaymentLineDialog.closeDialog();
          } else {
            const errorMsg = 'Upload failed\n' + result.errors.join('\n');
            const warningMsg =
              result.warnings && result.warnings.length > 0
                ? 'Additional warnings\n' + result.warnings.join('\n')
                : null;
            showErrorDialog(
              'MarketplacePaymentClient.importMarketplacePaymentFromFile',
              {
                message: errorMsg + (warningMsg ? '\n' + warningMsg : ''),
              }
            );
          }
        },
        (error) => {
          showErrorDialog(
            'MarketplacePaymentClient.importMarketplacePaymentFromFile',
            error
          );
        }
      );
    },
    [
      activeAccountWebClientConfig,
      bulkUploadMarketplacePaymentLineDialog,
      filterQuery,
      queryClient,
      refreshData,
      showErrorDialog,
    ]
  );

  return (
    <>
      <Button {...props} variant={variant} onClick={onBulkUploadPayment}>
        <UploadIcon
          withHoverEffect
          fill={IconsFill.currentColor}
          size={vars.iconSize.s}
        />
        <Content id={ContentId.BulkUpload} />
      </Button>
      <BulkUploadMarketplacePaymentLineDialog
        {...bulkUploadMarketplacePaymentLineDialog.dialogProps}
        onOkay={handleUploadSubmit}
        onClosed={bulkUploadMarketplacePaymentLineDialog.closeDialog}
        onCancel={bulkUploadMarketplacePaymentLineDialog.closeDialog}
      />
    </>
  );
};
